import { useCallback, useEffect, useMemo, useContext, useState } from 'react';
import { useSelector } from '@xstate/react';
import { isCanceling, stateLoggingListener, StepEvents, isSaving } from '@bingads-webui-campaign-react/step-wizard-machine';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { useExitWithoutSavingConfirmation } from '@bingads-webui-campaign-react/step-wizard';
import { serverErrorsCompare } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { useImpulse, usePrevProp } from '@bingads-webui-campaign-react/step-wizard-controls';
import timeZoneMapper from '@bingads-webui-component-legacy/timezonemapper/index';
import { isXandrDisplayAdsCampaign, CampaignSubTypeMapping, isXandrVideoAdsCampaign } from '@bingads-webui-campaign-react/constants';
import { needsSaving } from './step-wizard-config';
import { AgeRangesCampaignToAdInsight } from './target/age-range';
import { BidStrategyToDevices } from './target/device';

const doesNeedSavingSelector = state => !isCanceling(state) && needsSaving(state.context) && !isSaving(state);
const hideStepHeaderSelector = state => state.context.stepInfo.hideStepHeader;
const serverContextSelector = state => state.context.stepsData.serverContext;
const adGroupIdSelector = state => state.context.stepsData.adGroupId;
const adGroupSelector = state => state.context.data.adGroup;
const scopeSelector = state => state.context.stepsData.scope;
const locationTargetsSelector = state => state.context.data.adGroup.data.locationTargets;
const bundleCountriesSelector = state => state.context.data.bundleCountries;
const bundleLocationCriterionsSelector = state => state.context.data.bundleLocationCriterions;
const adSchedulesSelector = state => state.context.data.adGroup.data.adSchedules;
const adSchedulesBulkUpsertModelSelector = state => state.context.data.adGroup.data.adSchedulesBulkUpsert;
const genreTargetsSelector = state => state.context.data.adGroup.data.genreTargets;
const ageRangesSelector = state => state.context.data.adGroup.data.ageRanges;
const genderTypesSelector = state => state.context.data.adGroup.data.genderTypes;
const deviceTypesSelector = state => state.context.data.adGroup.data.deviceTypes;
const deviceRestrictionsSelector = state => state.context.data.adGroup.data.deviceRestrictions;
const deviceRestrictedSelector = state => state.context.data.adGroup.data.deviceRestricted;
const companyNameTargetsSelector = state => state.context.data.adGroup.data.companyNameTargets;
const negativeCompanyNameTargetsSelector = state => state.context.data.adGroup.data.negativeCompanyNameTargets;
const jobTargetsSelector = state => state.context.data.adGroup.data.jobTargets;
const negativeJobTargetsSelector = state => state.context.data.adGroup.data.negativeJobTargets;
const industrySelector = state => state.context.data.adGroup.data.industry;
const negativeIndustrySelector = state => state.context.data.adGroup.data.negativeIndustry;
const locationRestrictedSelector = state => state.context.data.adGroup.data.locationRestricted;
const dealLocationListSelector = state => state.context.data.adGroup.data.dealLocationList;
const genreRestrictedSelector = state => state.context.data.adGroup.data.genreRestricted;
const ageRestrictedSelector = state => state.context.data.adGroup.data.ageRestricted;
const genderRestrictedSelector = state => state.context.data.adGroup.data.genderRestricted;
const audienceRestrictedSelector = state => state.context.data.adGroup.data.audienceRestricted;
const customSegmentsSelector = state => state.context.data.adGroup.data.customSegments;
const campaignTimeZoneSelector = state => (state.context.stepsData.campaign && state.context.stepsData.campaign.TimeZone) || (state.context.stepsData.currentCampaign && state.context.stepsData.currentCampaign.TimeZone);
const campaignBiddingStrategySelector = state => (state.context.stepsData.campaign && state.context.stepsData.campaign.BiddingScheme) || (state.context.stepsData.currentCampaign && state.context.stepsData.currentCampaign.BiddingScheme);
const campaignSubTypeSelector = state => (state.context.stepsData.campaign && state.context.stepsData.campaign.CampaignSubType) || (state.context.stepsData.currentCampaign && state.context.stepsData.currentCampaign.CampaignSubType);
const campaignSettingsSelector = state => (state.context.stepsData.campaign && state.context.stepsData.campaign.CampaignSettings) || (state.context.stepsData.currentCampaign && state.context.stepsData.currentCampaign.CampaignSettings);
const systemErrorsSelector = state => state.context.data.systemErrors;
const isEditingSelector = state => state.context.stepsData.isEditing;
const serverErrorsSelector = state => state.context.data.serverErrors;
const invalidSaveAttemptSelector = state => state.context.invalidSaveAttempt;
const dealDetailsSelector = state => state.context.data.adGroup.data.deal;
const permissionsSelector = state => state.context.stepsData.permissions;
const placementsSelector = state => state.context.data.adGroup.data.placements;
const exclusionsSelector = state => state.context.data.adGroup.data.exclusions;
const initialAdGroupSelector = state => state.context.data.adGroup;


export const useStepAdGroupSettingsState = (stepService) => {
  const i18n = useContext(I18nContext);
  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap]), [i18n]);

  useEffect(() => {
    const subscription = stepService.subscribe(stateLoggingListener);
    return subscription.unsubscribe;
  }, [stepService]);

  const hideStepHeader = useSelector(stepService, hideStepHeaderSelector);
  const serverContext = useSelector(stepService, serverContextSelector);
  const adGroupId = useSelector(stepService, adGroupIdSelector);
  const adGroup = useSelector(stepService, adGroupSelector);
  const scope = useSelector(stepService, scopeSelector);
  const locationTargets = useSelector(stepService, locationTargetsSelector);
  const bundleCountries = useSelector(stepService, bundleCountriesSelector);
  const bundleLocationCriterions = useSelector(stepService, bundleLocationCriterionsSelector);
  const adSchedules = useSelector(stepService, adSchedulesSelector);
  const adSchedulesBulkUpsert = useSelector(stepService, adSchedulesBulkUpsertModelSelector);
  const genreTargets = useSelector(stepService, genreTargetsSelector);
  const ageRanges = useSelector(stepService, ageRangesSelector);
  const genderTypes = useSelector(stepService, genderTypesSelector);
  const deviceTypes = useSelector(stepService, deviceTypesSelector);
  const companyNameTargets = useSelector(stepService, companyNameTargetsSelector);
  const negativeCompanyNameTargets = useSelector(stepService, negativeCompanyNameTargetsSelector);
  const jobTargets = useSelector(stepService, jobTargetsSelector);
  const negativeJobTargets = useSelector(stepService, negativeJobTargetsSelector);
  const industry = useSelector(stepService, industrySelector);
  const negativeIndustry = useSelector(stepService, negativeIndustrySelector);
  const campaignTimeZone = useSelector(stepService, campaignTimeZoneSelector);
  const campaignBiddingStrategy = useSelector(stepService, campaignBiddingStrategySelector);
  const campaignSubtype = useSelector(stepService, campaignSubTypeSelector);
  const campaignSettings = useSelector(stepService, campaignSettingsSelector);
  const isEditing = useSelector(stepService, isEditingSelector);

  const doesNeedSaving = useSelector(stepService, doesNeedSavingSelector);
  const systemErrors = useSelector(stepService, systemErrorsSelector);

  const serverErrors = useSelector(stepService, serverErrorsSelector);
  const invalidSaveAttempt = useSelector(stepService, invalidSaveAttemptSelector);

  const dealDetails = useSelector(stepService, dealDetailsSelector);
  const deviceRestrictions = useSelector(stepService, deviceRestrictionsSelector);
  const deviceRestricted = useSelector(stepService, deviceRestrictedSelector);
  const locationRestricted = useSelector(stepService, locationRestrictedSelector);
  const genreRestricted = useSelector(stepService, genreRestrictedSelector);
  const ageRestricted = useSelector(stepService, ageRestrictedSelector);
  const genderRestricted = useSelector(stepService, genderRestrictedSelector);
  const audienceRestricted = useSelector(stepService, audienceRestrictedSelector);
  const customSegments = useSelector(stepService, customSegmentsSelector);
  const dealLocationList = useSelector(stepService, dealLocationListSelector);
  const permissions = useSelector(stepService, permissionsSelector);
  const placements = useSelector(stepService, placementsSelector);
  const exclusions = useSelector(stepService, exclusionsSelector);
  const [initialAdGroup] = useState(useSelector(stepService, initialAdGroupSelector));

  const [navigationConfirmationPrompt] = useExitWithoutSavingConfirmation(doesNeedSaving, stepService, i18n);

  const onAdGroupValidation = useCallback((results) => {
    const { data: newAdGroupData, ...restResults } = results;
    const dataPatch = {
      adGroup: {
        ...adGroup,
        data: {
          ...adGroup.data,
          ...newAdGroupData,
        },
        ...restResults,
      },
    };
    stepService.send({ type: StepEvents.UPDATE_VALIDATION, data: dataPatch });
  }, [stepService, adGroup]);

  // eslint-disable-next-line no-bitwise
  const [, hasInvalidSaveAttemptChanged] = usePrevProp(invalidSaveAttempt | 0);
  const [, hasServerErrorsChanged] = usePrevProp(serverErrors, serverErrorsCompare);
  const areBeaconsActive = useImpulse(hasInvalidSaveAttemptChanged || hasServerErrorsChanged);

  const [showAllValidationErrors, setShowAllValidationErrors] = useState(false);
  if (hasInvalidSaveAttemptChanged) {
    setShowAllValidationErrors(true);
  }

  const getLocationIds = useCallback(() => {
    if (locationTargets && locationTargets.currentLevelSettings) {
      const { targetOption, currentLocations } = locationTargets.currentLevelSettings;

      if (targetOption === 'bundle' && bundleCountries && bundleCountries.length > 0) {
        return bundleCountries.map(c => c.LocationId);
      }

      if (targetOption === 'specific' && currentLocations && currentLocations.length > 0) {
        return currentLocations.reduce((locationIds, cl) => {
          if (cl.SelectionStatus === 'Included' && cl.Criterion.LocationCriterion) {
            locationIds.push(cl.Criterion.LocationCriterion.LocationId);
          }
          return locationIds;
        }, []);
      }

      return [];
    }

    return [];
  }, [bundleCountries, locationTargets]);

  const getExcludedLocationIds = useCallback(() => {
    if (locationTargets && locationTargets.currentLevelSettings) {
      const { targetOption, currentLocations } = locationTargets.currentLevelSettings;

      if (targetOption === 'specific' && currentLocations && currentLocations.length > 0) {
        return currentLocations.reduce((locationIds, cl) => {
          if (cl.SelectionStatus === 'Excluded' && cl.Criterion.LocationCriterion) {
            locationIds.push(cl.Criterion.LocationCriterion.LocationId);
          }
          return locationIds;
        }, []);
      }

      return [];
    }

    return [];
  }, [locationTargets]);

  const getRadiusCriterions = useCallback(() => {
    if (locationTargets && locationTargets.currentLevelSettings) {
      const { currentLocations } = locationTargets.currentLevelSettings;
      if (currentLocations && currentLocations.length > 0) {
        return currentLocations.reduce((radiusTargets, cl) => {
          if (cl.CriterionType === 'Radius') {
            radiusTargets.push({
              CenterLatitude: cl.Criterion.RadiusCriterion.Latitude,
              CenterLongitude: cl.Criterion.RadiusCriterion.Longitude,
              Radius: cl.Criterion.RadiusCriterion.Radius,
            });
          }
          return radiusTargets;
        }, []);
      }
      return [];
    }
    return [];
  }, [locationTargets]);

  const getAudienceIds = useCallback(() => {
    if (adGroup && adGroup.data && adGroup.data.audiences && adGroup.data.audiences.length > 0) {
      return adGroup.data.audiences.reduce((includeIds, audience) => {
        if (audience.Status === 'Active') {
          includeIds.push(audience.Criterion.AudienceId);
        }
        return includeIds;
      }, []);
    }

    return [];
  }, [adGroup]);

  const getExcludedAudienceIds = useCallback(() => {
    if (adGroup && adGroup.data && adGroup.data.audiences && adGroup.data.audiences.length > 0) {
      return adGroup.data.audiences.reduce((includeIds, audience) => {
        if (audience.Status === 'Excluded') {
          includeIds.push(audience.Criterion.AudienceId);
        }
        return includeIds;
      }, []);
    }

    return [];
  }, [adGroup]);

  const getGenreTargets = useCallback(() => genreTargets, [genreTargets]);

  const getAgeRanges = useCallback(() => ageRanges && ageRanges.map(ar => AgeRangesCampaignToAdInsight[ar]), [ageRanges]);

  const getDevices = useCallback(() => {
    if (dealDetails) {
      return deviceTypes;
    }

    if (isXandrVideoAdsCampaign(campaignSubtype) && permissions.dynamic.XandrDeviceTarget && campaignBiddingStrategy.Type === 'ManualCpm') {
      return deviceTypes;
    }

    return BidStrategyToDevices[campaignBiddingStrategy.Type];
  }, [campaignBiddingStrategy.Type, campaignSubtype, dealDetails, deviceTypes, permissions.dynamic.XandrDeviceTarget]);

  const getCompanyNameTargets = useCallback(() => companyNameTargets, [companyNameTargets]);

  const getNegativeCompanyNameTargets = useCallback(() => negativeCompanyNameTargets, [negativeCompanyNameTargets]);

  const getJobTargets = useCallback(() => jobTargets, [jobTargets]);

  const getNegativeJobTargets = useCallback(() => negativeJobTargets, [negativeJobTargets]);

  const getIndustry = useCallback(() => industry, [industry]);

  const getNegativeIndustry = useCallback(() => negativeIndustry, [negativeIndustry]);

  const isAdScheduleInvalid = useMemo(() => adGroup && adGroup.messages && adGroup.messages.length > 0 && adGroup.messages.indexOf('Ad schedule has validation errors') !== -1, [adGroup]);

  const getAdSchedule = useCallback(() => {
    if (adGroup && adGroup.data && adGroup.data.adSchedulesBulkUpsert && adGroup.data.adSchedulesBulkUpsert.length > 0 && !isAdScheduleInvalid) {
      return adGroup.data.adSchedulesBulkUpsert.map((a) => {
        const { Day, FromHour, ToHour } = a.Criterion.DayTimeCriterion;
        return {
          Day,
          StartHour: FromHour,
          EndHour: ToHour,
        };
      });
    }
    return [];
  }, [adGroup, isAdScheduleInvalid]);

  const reachEstimationTargets = useMemo(() => ({
    Age: {
      Includes: getAgeRanges(),
    },
    Gender: {
      Includes: genderTypes,
    },
    Device: {
      Includes: getDevices(),
    },
    CompanyName: {
      Includes: getCompanyNameTargets(),
      Excludes: getNegativeCompanyNameTargets(),
    },
    Job: {
      Includes: getJobTargets(),
      Excludes: getNegativeJobTargets(),
    },
    Industry: {
      Includes: getIndustry(),
      Excludes: getNegativeIndustry(),
    },
    Location: {
      Includes: getLocationIds(),
      Excludes: getExcludedLocationIds(),
    },
    RadiusTargets: getRadiusCriterions(),
    Audience: {
      Includes: getAudienceIds(),
      Excludes: getExcludedAudienceIds(),
    },
    Genre: { Includes: getGenreTargets() },
    Day: getAdSchedule(),
    TimeZone: timeZoneMapper.getAccountIANATimeZone(),
    CampaignSubType: isXandrDisplayAdsCampaign(campaignSubtype) ? CampaignSubTypeMapping.DisplayAds : CampaignSubTypeMapping.BrandAwarenessVideoAds,
  }), [getAgeRanges, genderTypes, getDevices, getCompanyNameTargets, getNegativeCompanyNameTargets, getJobTargets, getNegativeJobTargets, getIndustry, getNegativeIndustry, getLocationIds, getRadiusCriterions, getExcludedLocationIds, getAudienceIds, getExcludedAudienceIds, getGenreTargets, getAdSchedule, campaignSubtype]);

  const state = {
    serverContext,
    adGroup,
    isEditing,
    i18n,
    legacyI18n,
    hideStepHeader,
    adGroupId,
    scope,
    locationTargets,
    bundleCountries,
    bundleLocationCriterions,
    adSchedules,
    adSchedulesBulkUpsert,
    genreTargets,
    ageRanges,
    genderTypes,
    deviceTypes,
    companyNameTargets,
    negativeCompanyNameTargets,
    jobTargets,
    negativeJobTargets,
    industry,
    negativeIndustry,
    campaignTimeZone,
    campaignBiddingStrategy,
    campaignSubtype,
    campaignSettings,
    dealDetails,
    systemErrors,
    navigationConfirmationPrompt,
    reachEstimationTargets,
    showAllValidationErrors,
    areBeaconsActive,
    serverErrors,
    deviceRestrictions,
    deviceRestricted,
    locationRestricted,
    genreRestricted,
    ageRestricted,
    dealLocationList,
    genderRestricted,
    audienceRestricted,
    customSegments,
    placements,
    exclusions,
    initialAdGroup,
  };
  const api = { onAdGroupValidation };

  return [state, api];
};
